import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, ArrowRight, BarChart3, TrendingUp, Loader2, FileText, Users, Target, Cpu, Shield, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import BackNavigation from "@/components/BackNavigation";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, Cell
} from "recharts";
import jsPDF from "jspdf";

// Design system: ONLY Red + Blue
const INDEX_COLORS = {
  trust: "#1E3A8A",      // Primary Blue
  alignment: "#2563EB",  // Blue
  readiness: "#3B82F6",  // Blue shade
  ai_capability: "#60A5FA", // Blue shade
  resilience: "#93C5FD"  // Blue shade
};

const INDEX_LABELS = {
  trust: "Trust",
  alignment: "Alignment",
  readiness: "Readiness",
  ai_capability: "AI Capability",
  resilience: "Resilience"
};

const INDEX_ICONS = {
  trust: Users,
  alignment: Target,
  readiness: TrendingUp,
  ai_capability: Cpu,
  resilience: Shield
};

export default function ObservatoryResults() {
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    const storedResult = sessionStorage.getItem("surveyResult");
    if (storedResult) {
      setResult(JSON.parse(storedResult));
      setLoading(false);
    } else {
      toast.error("No diagnostic results found. Please complete the diagnostic first.");
      navigate("/observatory/survey");
    }
  }, [navigate]);

  const generatePDF = async () => {
    if (!result) return;
    setGenerating(true);
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 20;
      let yPos = margin;

      // Header
      pdf.setFontSize(10);
      pdf.setTextColor(153, 27, 27);
      pdf.text('ZODIAC WORKFORCE INTELLIGENCE INSTITUTE', margin, yPos);
      yPos += 12;

      pdf.setFontSize(22);
      pdf.setTextColor(17, 24, 39);
      pdf.text('Workforce Intelligence', margin, yPos);
      yPos += 8;
      pdf.text('Diagnostic Report', margin, yPos);
      yPos += 12;

      pdf.setFontSize(10);
      pdf.setTextColor(75, 85, 99);
      pdf.text(`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, margin, yPos);
      yPos += 6;
      pdf.text(`Industry: ${result.metadata.industry}`, margin, yPos);
      yPos += 6;
      pdf.text(`Organization Size: ${result.metadata.organization_size}`, margin, yPos);
      yPos += 15;

      // Main Score Box
      pdf.setFillColor(30, 58, 138);
      pdf.roundedRect(margin, yPos, pageWidth - margin * 2, 35, 3, 3, 'F');
      pdf.setFontSize(12);
      pdf.setTextColor(255, 255, 255);
      pdf.text('WORKFORCE INTELLIGENCE INDEX (WII)', margin + 10, yPos + 12);
      pdf.setFontSize(28);
      pdf.text(`${result.wii_score} / 10`, margin + 10, yPos + 28);
      pdf.setFontSize(10);
      pdf.text(result.classification, pageWidth - margin - 10 - pdf.getTextWidth(result.classification), yPos + 22);
      yPos += 45;

      // Index Scores - All Blue shades
      pdf.setFontSize(14);
      pdf.setTextColor(153, 27, 27);
      pdf.text('Index Breakdown', margin, yPos);
      yPos += 10;

      const indices = [
        { name: 'Trust Index', score: result.indices.trust, color: [30, 58, 138] },
        { name: 'Alignment Index', score: result.indices.alignment, color: [37, 99, 235] },
        { name: 'Readiness Index', score: result.indices.readiness, color: [59, 130, 246] },
        { name: 'AI Capability Index', score: result.indices.ai_capability, color: [96, 165, 250] },
        { name: 'Resilience Index', score: result.indices.resilience, color: [147, 197, 253] }
      ];

      indices.forEach(index => {
        pdf.setFillColor(...index.color);
        const barWidth = (index.score / 10) * (pageWidth - margin * 2 - 80);
        pdf.roundedRect(margin + 70, yPos - 4, barWidth, 8, 2, 2, 'F');
        pdf.setFontSize(10);
        pdf.setTextColor(17, 24, 39);
        pdf.text(index.name, margin, yPos);
        pdf.text(`${index.score}`, pageWidth - margin - 10, yPos);
        yPos += 14;
      });
      yPos += 10;

      // Benchmark Comparison
      pdf.setFontSize(14);
      pdf.setTextColor(153, 27, 27);
      pdf.text('Benchmark Comparison', margin, yPos);
      yPos += 10;

      pdf.setFontSize(10);
      pdf.setTextColor(75, 85, 99);
      pdf.text(`Your Score: ${result.wii_score}`, margin, yPos);
      yPos += 6;
      pdf.text(`Industry Average (${result.benchmark_comparison.industry}): ${result.benchmark_comparison.industry_average}`, margin, yPos);
      yPos += 6;
      pdf.text(`Global Average (GWII): ${result.benchmark_comparison.global_average}`, margin, yPos);
      yPos += 15;

      // AI Insight
      pdf.setFontSize(14);
      pdf.setTextColor(153, 27, 27);
      pdf.text('AI Insight', margin, yPos);
      yPos += 8;
      pdf.setFontSize(9);
      pdf.setTextColor(75, 85, 99);
      const insightLines = pdf.splitTextToSize(result.ai_insight, pageWidth - margin * 2);
      insightLines.forEach(line => {
        pdf.text(line, margin, yPos);
        yPos += 5;
      });
      yPos += 10;

      // Recommendations
      pdf.setFontSize(14);
      pdf.setTextColor(153, 27, 27);
      pdf.text('Recommendations', margin, yPos);
      yPos += 8;

      pdf.setFontSize(9);
      result.recommendations.forEach((rec, i) => {
        pdf.setTextColor(30, 58, 138);
        pdf.text(`${i + 1}.`, margin, yPos);
        pdf.setTextColor(75, 85, 99);
        const recLines = pdf.splitTextToSize(rec, pageWidth - margin * 2 - 10);
        recLines.forEach((line) => {
          pdf.text(line, margin + 8, yPos);
          yPos += 5;
        });
        yPos += 3;
      });

      // Footer
      yPos = pdf.internal.pageSize.getHeight() - 15;
      pdf.setFontSize(8);
      pdf.setTextColor(180, 180, 180);
      pdf.text('© 2026 Zodiac Workforce Intelligence Institute. All rights reserved.', margin, yPos);

      pdf.save(`WII-Diagnostic-Report-${result.id.slice(0, 8)}.pdf`);
      toast.success("Report downloaded successfully!");
      
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("Failed to generate PDF. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" />
      </div>
    );
  }

  if (!result) return null;

  // Chart data - All Blue shades
  const indicesBarData = Object.entries(result.indices).map(([key, value]) => ({
    name: INDEX_LABELS[key],
    score: value,
    fill: INDEX_COLORS[key]
  }));

  const radarData = Object.entries(result.indices).map(([key, value]) => ({
    subject: INDEX_LABELS[key],
    score: value,
    benchmark: result.benchmark_comparison.indices_comparison[key]?.benchmark || 6.5
  }));

  // Benchmark comparison - Blue shades
  const benchmarkComparisonData = [
    { name: 'Your Score', value: result.wii_score, fill: '#1E3A8A' },
    { name: 'Industry Avg', value: result.benchmark_comparison.industry_average, fill: '#3B82F6' },
    { name: 'Global Avg', value: result.benchmark_comparison.global_average, fill: '#93C5FD' }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-8 sm:py-12" data-testid="results-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Back Navigation */}
        <div className="mb-6">
          <BackNavigation fallbackPath="/observatories/global" fallbackLabel="Global Observatory" />
        </div>

        {/* Header */}
        <motion.div 
          className="text-center mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-sm font-medium text-[#991B1B] uppercase tracking-wider">Diagnostic Complete</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold mt-2 mb-2" style={{ color: '#991B1B' }} data-testid="results-title">
            Workforce Intelligence Results
          </h1>
          <p className="text-[#4B5563]">
            {result.metadata.industry} · {result.metadata.organization_size}
          </p>
        </motion.div>

        {/* Main Score Card - Blue */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <Card className="mb-8 border-0 shadow-xl bg-[#1E3A8A] text-white" data-testid="main-score-card">
            <CardContent className="p-8 sm:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="text-center md:text-left">
                  <p className="text-white/60 text-sm uppercase tracking-wider mb-2">Workforce Intelligence Index</p>
                  <div className="flex items-baseline gap-2 justify-center md:justify-start">
                    <span className="font-mono text-7xl sm:text-8xl font-bold" data-testid="wii-score">{result.wii_score}</span>
                    <span className="text-3xl text-white/60">/ 10</span>
                  </div>
                  <div className="mt-4">
                    <span className={`inline-block px-4 py-1 rounded-full text-sm font-medium ${
                      result.wii_score >= 7.5 ? 'bg-blue-400/30 text-blue-100' :
                      result.wii_score >= 6.0 ? 'bg-blue-300/30 text-blue-100' :
                      'bg-red-500/30 text-red-200'
                    }`} data-testid="classification">
                      {result.classification}
                    </span>
                  </div>
                </div>
                <div className="bg-white/10 rounded-xl p-6">
                  <p className="text-white/60 text-sm mb-4 uppercase tracking-wider">Benchmark Comparison</p>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span>Your Score</span>
                      <span className="font-mono font-bold text-lg">{result.wii_score}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/80">{result.metadata.industry} Average</span>
                      <span className="font-mono text-blue-200">{result.benchmark_comparison.industry_average}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/80">Global Average (GWII)</span>
                      <span className="font-mono text-blue-300">{result.benchmark_comparison.global_average}</span>
                    </div>
                    <div className="flex justify-between border-t border-white/20 pt-3 mt-3">
                      <span>vs. Industry</span>
                      <span className={`font-mono font-bold ${result.benchmark_comparison.industry_difference >= 0 ? 'text-blue-200' : 'text-red-300'}`}>
                        {result.benchmark_comparison.industry_difference >= 0 ? '+' : ''}{result.benchmark_comparison.industry_difference}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Five Index Cards - Blue shades */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8"
        >
          {Object.entries(result.indices).map(([key, value]) => {
            const Icon = INDEX_ICONS[key];
            const heatmap = result.heatmap_data?.find(h => h.index === key);
            const isRisk = value < 6;
            return (
              <Card key={key} className="border-[#E5E7EB]" data-testid={`index-card-${key}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: isRisk ? '#991B1B20' : `${INDEX_COLORS[key]}20` }}>
                      <Icon className="w-4 h-4" style={{ color: isRisk ? '#991B1B' : INDEX_COLORS[key] }} />
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      value >= 8 ? 'bg-blue-100 text-blue-800' :
                      value >= 6 ? 'bg-blue-50 text-blue-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {value >= 8 ? 'Strong' : value >= 6 ? 'Moderate' : 'Risk'}
                    </span>
                  </div>
                  <p className="font-mono text-3xl font-bold" style={{ color: isRisk ? '#991B1B' : INDEX_COLORS[key] }}>{value}</p>
                  <p className="text-xs text-[#4B5563] mt-1">{INDEX_LABELS[key]} Index</p>
                </CardContent>
              </Card>
            );
          })}
        </motion.div>

        {/* AI Insight */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="mb-8"
        >
          <Card className="border-[#E5E7EB]" data-testid="ai-insight-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2" style={{ color: '#991B1B' }}>
                <Sparkles className="w-5 h-5" />
                AI Insight
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#4B5563] leading-relaxed">{result.ai_insight}</p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Heatmap - Blue + Red only */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.27 }}
          className="mb-8"
        >
          <Card className="border-[#E5E7EB]" data-testid="heatmap-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg" style={{ color: '#991B1B' }}>Workforce Intelligence Heatmap</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-5 gap-2">
                {result.heatmap_data?.map((item) => {
                  const isRisk = item.score < 6;
                  const bgColor = isRisk ? '#991B1B' : item.score >= 8 ? '#1E3A8A' : '#3B82F6';
                  return (
                    <div
                      key={item.index}
                      className="rounded-lg p-4 text-center text-white"
                      style={{ backgroundColor: bgColor }}
                      data-testid={`heatmap-${item.index}`}
                    >
                      <p className="font-mono text-2xl font-bold">{item.score}</p>
                      <p className="text-xs mt-1 capitalize">{INDEX_LABELS[item.index]}</p>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-center gap-6 mt-4 text-xs">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded bg-[#1E3A8A]"></span>
                  <span className="text-[#4B5563]">Strong (≥8)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded bg-[#3B82F6]"></span>
                  <span className="text-[#4B5563]">Moderate (6-7.9)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded bg-[#991B1B]"></span>
                  <span className="text-[#4B5563]">Risk (&lt;6)</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Charts Row - Blue shades */}
        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* Index Breakdown Bar Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Card className="border-[#E5E7EB] h-full" data-testid="breakdown-chart">
              <CardHeader>
                <CardTitle className="font-heading text-lg" style={{ color: '#991B1B' }}>Index Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={indicesBarData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis type="number" domain={[0, 10]} tick={{ fontSize: 12, fill: '#374151' }} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#374151' }} width={90} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1E3A8A', border: 'none', borderRadius: '6px' }}
                        labelStyle={{ color: 'white' }}
                        itemStyle={{ color: 'white' }}
                        formatter={(value) => [`${value} / 10`, 'Score']}
                      />
                      <Bar dataKey="score" radius={[0, 4, 4, 0]}>
                        {indicesBarData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Benchmark Comparison Bar Chart - Blue shades */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
          >
            <Card className="border-[#E5E7EB] h-full" data-testid="benchmark-bar-chart">
              <CardHeader>
                <CardTitle className="font-heading text-lg" style={{ color: '#991B1B' }}>Industry Benchmarks</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={benchmarkComparisonData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#374151' }} />
                      <YAxis domain={[0, 10]} tick={{ fontSize: 12, fill: '#374151' }} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1E3A8A', border: 'none', borderRadius: '6px' }}
                        labelStyle={{ color: 'white' }}
                        itemStyle={{ color: 'white' }}
                        formatter={(value) => [`${value}`, 'WII Score']}
                      />
                      <Legend />
                      <Bar dataKey="value" name="WII Score" radius={[4, 4, 0, 0]}>
                        {benchmarkComparisonData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Radar Chart - Blue + Blue shade */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-8"
        >
          <Card className="border-[#E5E7EB]" data-testid="radar-chart">
            <CardHeader>
              <CardTitle className="font-heading text-lg" style={{ color: '#991B1B' }}>Workforce Intelligence Profile</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="#E5E7EB" />
                    <PolarAngleAxis dataKey="subject" tick={{ fontSize: 12, fill: '#374151' }} />
                    <PolarRadiusAxis angle={30} domain={[0, 10]} tick={{ fontSize: 10, fill: '#374151' }} />
                    <Radar name="Your Organization" dataKey="score" stroke="#1E3A8A" fill="#1E3A8A" fillOpacity={0.4} strokeWidth={2} />
                    <Radar name="Industry Average" dataKey="benchmark" stroke="#93C5FD" fill="#93C5FD" fillOpacity={0.2} strokeWidth={2} />
                    <Legend />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Recommendations - Blue accent */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.45 }}
          className="mb-8"
        >
          <Card className="border-[#E5E7EB]" data-testid="recommendations-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2" style={{ color: '#991B1B' }}>
                <TrendingUp className="w-5 h-5" />
                Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {result.recommendations.map((rec, index) => (
                  <div 
                    key={index} 
                    className="flex items-start gap-4 p-4 bg-blue-50 border-l-4 border-[#1E3A8A] rounded-r-lg"
                    data-testid={`recommendation-${index}`}
                  >
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#1E3A8A]/20 flex items-center justify-center font-mono text-sm font-bold text-[#1E3A8A]">
                      {index + 1}
                    </span>
                    <p className="text-[#111827] pt-0.5">{rec}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <Button
            onClick={generatePDF}
            disabled={generating}
            className="gap-2 bg-[#991B1B] text-white hover:bg-[#7F1D1D] h-12 px-8"
            data-testid="download-pdf-button"
          >
            {generating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Generating Report...
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                Download Report
              </>
            )}
          </Button>
          
          <Link to="/juswork/observatory/dashboard">
            <Button variant="outline" className="gap-2 h-12 px-8 border-[#E5E7EB]" data-testid="view-dashboard-button">
              <BarChart3 className="w-5 h-5" />
              Explore Dashboard
            </Button>
          </Link>
          
          <Link to="/juswork/observatory/survey">
            <Button variant="outline" className="gap-2 h-12 px-8 border-[#E5E7EB]" data-testid="retake-button">
              Retake Diagnostic
              <ArrowRight className="w-5 h-5" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
